import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Coins, Loader2, Sparkles } from "lucide-react";
import type { SaleskoBillingProduct } from "@salesko/contracts";
import { getBillingCatalog, readStoredSaleskoSession, startCheckout, writePendingBillingCheckoutIntent } from "../lib/api";

function priceLabel(product: SaleskoBillingProduct): string {
  const dollars = (product.priceCents / 100).toLocaleString(undefined, {
    minimumFractionDigits: product.priceCents % 100 === 0 ? 0 : 2
  });
  return `$${dollars}`;
}

function intervalSuffix(product: SaleskoBillingProduct): string {
  if (product.type !== "subscription") {
    return "one-time";
  }
  return product.interval === "year" ? "/year" : "/month";
}

export function PricingPage() {
  const catalogQuery = useQuery({
    queryKey: ["billing-catalog"],
    queryFn: getBillingCatalog,
    retry: 1,
    staleTime: 60_000
  });

  // The CTA is per-product, so track which one is mid-flight.
  const checkoutMutation = useMutation({
    mutationFn: (productId: string) => startCheckout(productId, "/settings/billing"),
    onSuccess: (response) => {
      if (typeof window !== "undefined") {
        window.location.assign(response.checkoutUrl);
      }
    }
  });

  const onBuy = (productId: string) => {
    // Checkout needs a tenant session; send signed-out visitors to login first.
    if (!readStoredSaleskoSession()) {
      if (typeof window !== "undefined") {
        writePendingBillingCheckoutIntent(productId, "/settings/billing");
        window.location.assign("/login");
      }
      return;
    }
    checkoutMutation.mutate(productId);
  };

  const products = catalogQuery.data?.products ?? [];
  // Lead with monthly subscription tiers; show one-time credit packs below.
  const tiers = products.filter((p) => p.type === "subscription" && p.interval === "month");
  const packs = products.filter((p) => p.type === "one_time");

  return (
    <main className="pricing-page">
      <header className="pricing-head">
        <p className="eyebrow">Pricing</p>
        <h1>Plans for Generic Agent, MCP, and Deep Research</h1>
        <p className="pricing-sub">
          Generic Agent is free with a daily allowance. Pro raises the quota and unlocks MCP access; Business adds
          SalesKo-managed Deep Research credits.
        </p>
      </header>

      {catalogQuery.isLoading ? (
        <p className="pricing-loading">
          <Loader2 className="spin" size={18} /> Loading plans…
        </p>
      ) : null}

      <section className="pricing-grid" aria-label="Subscription plans">
        {tiers.map((product) => (
          <article
            className={`pricing-card${product.tier === "business_managed" ? " pricing-card-featured" : ""}`}
            key={product.productId}
          >
            {product.tier === "business_managed" ? <span className="pricing-badge">Most popular</span> : null}
            <p className="eyebrow">{product.name}</p>
            <p className="pricing-price">
              <strong>{priceLabel(product)}</strong>
              <span className="pricing-interval">{intervalSuffix(product)}</span>
            </p>
            {product.privateAgentAccessMode === "byok" ? (
              <p className="pricing-credits">
                <Coins size={14} /> Generic Agent: 200 messages / day
              </p>
            ) : (
              <p className="pricing-credits">
                <Coins size={14} /> {product.credits.toLocaleString()} credits / month
              </p>
            )}
            <p className="pricing-desc">{product.description}</p>
            <ul className="pricing-features">
              {product.privateAgentAccessMode === "byok" ? (
                <li>
                  <Check size={14} /> 200 Generic Agent messages per day
                </li>
              ) : (
                <li>
                  <Check size={14} /> Unlimited Generic Agent messages
                </li>
              )}
              <li>
                <Check size={14} /> MCP access for external agents
              </li>
              <li>
                <Check size={14} />{" "}
                {product.privateAgentAccessMode === "byok"
                  ? "Deep Research can use your provider key"
                  : `${product.credits.toLocaleString()} SalesKo-managed Deep Research credits`}
              </li>
            </ul>
            <button
              className="secondary-button pricing-cta"
              disabled={checkoutMutation.isPending}
              onClick={() => onBuy(product.productId)}
              type="button"
            >
              {checkoutMutation.isPending && checkoutMutation.variables === product.productId ? (
                <Loader2 className="spin" size={15} />
              ) : (
                <Sparkles size={15} />
              )}
              <span>Choose {product.name}</span>
            </button>
          </article>
        ))}
      </section>

      {packs.length > 0 ? (
        <section className="pricing-packs" aria-label="Credit packs">
          <p className="eyebrow">Need more credits?</p>
          <div className="pricing-pack-row">
            {packs.map((pack) => (
              <article className="pricing-pack" key={pack.productId}>
                <p className="pricing-pack-name">{pack.name}</p>
                <p className="pricing-pack-credits">
                  <Coins size={14} /> {pack.credits.toLocaleString()} credits
                </p>
                <p className="pricing-pack-price">{priceLabel(pack)}</p>
                <button
                  className="secondary-button pricing-cta"
                  disabled={checkoutMutation.isPending}
                  onClick={() => onBuy(pack.productId)}
                  type="button"
                >
                  <span>Buy pack</span>
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {checkoutMutation.isError ? (
        <p className="history-error" role="alert">
          Could not start checkout. Please try again shortly.
        </p>
      ) : null}
    </main>
  );
}
